import {format, fromUnixTime} from "date-fns";
import {Trip} from "./types/Trip";
import {TotalMinutesAndDistanceType} from "./types/General";
import {AddressType, isAddressCompleted} from "./types/Address";
import axios from "axios";
import General from "./constants/General";
import {LanguagesIds} from "./lang/i18n";
import {deconstructTaskId, TaskType} from "./types/Task";

export const getTaskFromTrip = (trip: Trip, taskId: string): TaskType | null => {
    for (let i = 0; i <= (trip?.Address.length ?? 0); i++) {
        const Task = getTaskFromAddress(trip?.Address[i], taskId);

        if (Task) {
            return Task;
        }
    }

    return null;
}

export const getTaskFromAddress = (address: AddressType, taskId: string): TaskType | null => {
    return address.Tasks.find((task => task.TaskId === taskId)) ?? null;
}

export const fetchTrip = async (
    TripKey: string,
    LanguageId: LanguagesIds,
    TourId: string | null = null,
): Promise<Trip | undefined> => {
    const {data} = await axios.post(`${General.apiUrl}/driver-tasks`, {
        LanguageId,
        TripKey,
        TourId,
        AppVersion: 'cloudex-tracking',
    });

    return data.Content.Trip[0] ?? undefined;
}

export const getFileName = (fileName: string, originalFilename: string): string => {
    return fileName + '_' + formatTimestampAsDate(Date.now()) + '.' + originalFilename.split('.').pop();
}

export const formatTimestampAsDate = (timestamp: number | string): string => {
    // 2024-12-12T07-45-49.580Z
    return format(fromUnixTime(Number(timestamp)), 'y-MM-dd\'T\'HH-mm-ss\'.000Z\'');
}

export const formatDateForTask = (date: Date) => {
    // 2024-12-12T07:45:49.580Z
    return format(date, 'yyyy-MM-dd\'T\'HH:mm:ss\'.000Z\'');
}

export const base64ToFile = async (base64: string, fileName: string) => {
    const img = await fetch(base64);
    const blob = await img.blob();

    return new File([blob], fileName, {type: 'image/jpg'});
}

export const convertBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);

        fileReader.onload = () => {
            resolve(String(fileReader.result));
        };

        fileReader.onerror = (error) => {
            reject(error);
        };
    });
};

export const canBeDelivered = (Task: TaskType, Trip: Trip): boolean => {
    if (Task.TaskCompletedDate !== '') {
        return false;
    }

    if (Task.TaskType === 1 || Task.TaskType === 5) {
        return true;
    }

    const taskIdDeconstructed = deconstructTaskId(Task.TaskId);

    Trip.Address.forEach((Address: AddressType) => {
        const TaskFound: TaskType | undefined = Address.Tasks.find((Task: TaskType) => Task.TaskId === '1_' + taskIdDeconstructed);
        if (TaskFound !== undefined) {
            return TaskFound.TaskCompletedDate !== '';
        }
    });

    return true;
}

export const getTotalDistanceAndMinutesFromAddress = (Trip: Trip): TotalMinutesAndDistanceType => {
    let totalMin = 0;
    let totalKm = 0;

    Trip.Address.forEach((Address: AddressType) => {
        const shouldBeSkipped = Address && isAddressCompleted(Address);
        if (shouldBeSkipped) {
            return;
        }

        totalMin = Math.round(totalMin + Number(Address.MinTillNextStop));
        totalKm = Math.round(totalKm + Number(Address.KmTillNextStop));
    });

    return {Min: totalMin, Km: totalKm};
}

export const toHoursAndMinutes = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) {
        return minutes + ' min';
    }

    if (minutes === 0) {
        return hours + ' h';
    }

    return hours + ' h, ' + minutes + ' min';
}
